import React, { useMemo } from 'react';
import * as THREE from 'three';
import { ThickLine } from '../shared3d/LineRenderers';
import EdgeProtector from '../shared3d/EdgeProtector';

interface DirectLashingProps {
  startPoint: [number, number, number];
  endPoint: [number, number, number];
  midPoint?: [number, number, number];
  color: string;
  startRotation?: [number, number, number];
}

interface LashingAnchorProps {
  position: [number, number, number];
  color?: string;
  rotation?: [number, number, number];
  type?: 'ground' | 'cargo';
}

const LashingAnchor: React.FC<LashingAnchorProps> = ({
  position,
  color = '#cbd5e1',
  rotation = [Math.PI / 2, 0, 0],
  type = 'ground'
}) => {
  return (
    <group position={position} rotation={rotation}>
      <mesh castShadow>
        <torusGeometry args={[0.05, 0.01, 16, 32]} />
        <meshStandardMaterial
          color={color}
          metalness={0.8}
          roughness={0.2}
          emissive={color}
          emissiveIntensity={0.1}
        />
      </mesh>
      <mesh position={[0, 0, -0.01]}>
        {type === 'ground' ? (
          <cylinderGeometry args={[0.03, 0.035, 0.015, 16]} />
        ) : (
          <boxGeometry args={[0.14, 0.08, 0.015]} />
        )}
        <meshStandardMaterial color="#94a3b8" metalness={0.6} />
      </mesh>
    </group>
  );
};

export const DirectLashing: React.FC<DirectLashingProps> = ({
  startPoint,
  endPoint,
  midPoint,
  color,
  startRotation
}) => {
  const points = useMemo(() => {
    const pts = [new THREE.Vector3(...startPoint)];
    if (midPoint) pts.push(new THREE.Vector3(...midPoint));
    pts.push(new THREE.Vector3(...endPoint));
    return pts;
  }, [startPoint, midPoint, endPoint]);

  return (
    <group>
      <ThickLine points={points} color={color} width={0.04} thickness={0.005} />
      <LashingAnchor position={endPoint} type="ground" />
      <LashingAnchor position={startPoint} rotation={startRotation} type="cargo" />
    </group>
  );
};

interface LoopLashingProps {
  position: [number, number, number];
  depth: number;
  height: number;
  color: string;
  anchorSide?: 'right' | 'left' | 'forward' | 'backward';
  anchorZ?: number; // Distance for 'z' axis loops
  anchorX?: number; // Distance for 'x' axis loops  
  anchorYOffset?: number;
  axis?: 'x' | 'z';
}

export const LoopLashing: React.FC<LoopLashingProps> = ({
  position,
  depth,
  height,
  color,
  anchorSide = 'right',
  anchorZ,
  anchorX,
  anchorYOffset = 0,
  axis = 'z'
}) => {
  const { points, groundPoint, edgePoints } = useMemo(() => {
    const halfD = depth / 2;
    const groundOffset = 1.0;

    const yBottom = anchorYOffset;
    const yTop = height;

    if (axis === 'x') {
      // Loop wraps across Ship Longitudinal axis (X)
      // anchorSide will be 'forward' or 'backward'
      const xCargoPos = halfD;
      const xCargoNeg = -halfD;
      const xAnchorPos = anchorX !== undefined && (anchorSide === 'forward' || anchorSide === 'right') ? anchorX : halfD + groundOffset;
      const xAnchorNeg = anchorX !== undefined && (anchorSide === 'backward' || anchorSide === 'left') ? anchorX : -halfD - groundOffset;

      const points = (anchorSide === 'forward' || anchorSide === 'right')
        ? [
          new THREE.Vector3(xAnchorPos, yBottom, 0),
          new THREE.Vector3(xCargoPos, yTop, 0),
          new THREE.Vector3(xCargoNeg, yTop, 0),
          new THREE.Vector3(xCargoNeg, yBottom, 0),
          new THREE.Vector3(xAnchorPos, yBottom, 0),
        ]
        : [
          new THREE.Vector3(xAnchorNeg, yBottom, 0),
          new THREE.Vector3(xCargoNeg, yTop, 0),
          new THREE.Vector3(xCargoPos, yTop, 0),
          new THREE.Vector3(xCargoPos, yBottom, 0),
          new THREE.Vector3(xAnchorNeg, yBottom, 0),
        ];

      return {
        points,
        groundPoint: (anchorSide === 'forward' || anchorSide === 'right')
          ? [xAnchorPos, yBottom, 0] as [number, number, number]
          : [xAnchorNeg, yBottom, 0] as [number, number, number],
        edgePoints: [
          { pos: [xCargoPos, yTop, 0] as [number, number, number], rot: [0, Math.PI / 2, 0] as [number, number, number] },
          { pos: [xCargoNeg, yTop, 0] as [number, number, number], rot: [0, -Math.PI / 2, 0] as [number, number, number] }
        ]
      };
    } else {
      // Default: Loop wraps across Ship Transverse axis (Z)
      const zCargoPos = halfD;
      const zCargoNeg = -halfD;
      const zAnchorPos = anchorZ !== undefined && anchorSide === 'right' ? anchorZ : halfD + groundOffset;
      const zAnchorNeg = anchorZ !== undefined && anchorSide === 'left' ? anchorZ : -halfD - groundOffset;

      const points = anchorSide === 'right'
        ? [
          new THREE.Vector3(0, yBottom, zAnchorPos),
          new THREE.Vector3(0, yTop, zCargoPos),
          new THREE.Vector3(0, yTop, zCargoNeg),
          new THREE.Vector3(0, yBottom, zCargoNeg),
          new THREE.Vector3(0, yBottom, zAnchorPos),
        ]
        : [
          new THREE.Vector3(0, yBottom, zAnchorNeg),
          new THREE.Vector3(0, yTop, zCargoNeg),
          new THREE.Vector3(0, yTop, zCargoPos),
          new THREE.Vector3(0, yBottom, zCargoPos),
          new THREE.Vector3(0, yBottom, zAnchorNeg),
        ];

      return {
        points,
        groundPoint: anchorSide === 'right'
          ? [0, yBottom, zAnchorPos] as [number, number, number]
          : [0, yBottom, zAnchorNeg] as [number, number, number],
        edgePoints: [
          { pos: [0, yTop, zCargoPos] as [number, number, number], rot: [0, 0, 0] as [number, number, number] },
          { pos: [0, yTop, zCargoNeg] as [number, number, number], rot: [0, Math.PI, 0] as [number, number, number] }
        ]
      };
    }
  }, [depth, height, anchorSide, anchorZ, anchorX, axis, anchorYOffset]);

  return (
    <group position={position}>
      <ThickLine points={points} color={color} width={0.04} thickness={0.005} />
      <LashingAnchor position={groundPoint} />
      {edgePoints.map((edge, idx) => (
        <EdgeProtector key={`edge-${idx}`} position={edge.pos} rotation={edge.rot} />
      ))}
    </group>
  );
};

interface SupportBlockProps {
  position: [number, number, number];
  size: [number, number, number]; // [thickness, height, width] - thickness is sticking out from cargo
  type: 'stopper' | 'wedge';
  side: 'forward' | 'backward' | 'right' | 'left';
}

export const SupportBlock: React.FC<SupportBlockProps> = ({ position, size, type, side }) => {
  const [thickness, h, width] = size;

  // Determine rotation based on side
  // Default (Forward): extends along +X
  let rotation: [number, number, number] = [0, 0, 0];
  if (side === 'backward') rotation = [0, Math.PI, 0];
  if (side === 'right') rotation = [0, -Math.PI / 2, 0];
  if (side === 'left') rotation = [0, Math.PI / 2, 0];

  if (type === 'stopper') {
    return (
      <group position={position} rotation={rotation}>
        {/* Main Pillar/Body - Super Bright Metallic White */}
        <mesh castShadow>
          <boxGeometry args={[thickness, h * 0.8, width * 0.9]} />
          <meshStandardMaterial color="#ffffff" metalness={0.8} roughness={0.1} />
        </mesh>

        {/* Top Compression Plate - Brighter Polished Silver */}
        <mesh position={[0, h * 0.42, 0]} castShadow>
          <boxGeometry args={[thickness * 1.1, h * 0.2, width * 1.1]} />
          <meshStandardMaterial color="#f1f5f9" metalness={0.98} roughness={0.01} />
        </mesh>

        {/* Bottom Base Plate (Touches cargo) - Brighter Polished Silver */}
        <mesh position={[0, -h * 0.42, 0]} castShadow>
          <boxGeometry args={[thickness * 1.1, h * 0.2, width * 1.1]} />
          <meshStandardMaterial color="#f1f5f9" metalness={0.98} roughness={0.01} />
        </mesh>

        {/* High-Visibility Indicator (the little gold part) */}
        <mesh position={[-thickness * 0.3, h * 0.35, width * 0.4]}>
          <boxGeometry args={[thickness * 0.15, h * 0.15, width * 0.15]} />
          <meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={0.5} />
        </mesh>

        {/* Floor Mounting/Reinforcement Plate - Light Steel Gray */}
        <mesh position={[0, -h * 0.5, 0]}>
          <boxGeometry args={[thickness * 1.2, h * 0.1, width * 1.3]} />
          <meshStandardMaterial color="#e2e8f0" metalness={0.9} roughness={0.1} />
        </mesh>
      </group>
    );
  } else {
    // Wedge / Dunnage
    const color = '#FFA500';
    const edgeColor = '#8B4513';
    return (
      <group position={position} rotation={rotation}>
        <mesh castShadow>
          <boxGeometry args={size} />
          <meshStandardMaterial color={color} roughness={0.8} />
        </mesh>
        <lineSegments>
          <edgesGeometry args={[new THREE.BoxGeometry(...size)]} />
          <lineBasicMaterial color={edgeColor} />
        </lineSegments>
      </group>
    );
  }
};
